import moment, {Moment} from 'moment-timezone'

import {
  IncomingMaterialStatus,
  MaterialOrderStatus,
  type IncomingMaterial,
  type CanceledIncomingMaterial,
  type PlannedIncomingMaterial,
  type DeliveredIncomingMaterial,
  type UnplannedIncomingMaterial,
  type MaterialOrder,
  type GroupedUnplannedDelivery
} from '../types'

export const splitOrdersToPastAndFuture = (orders: MaterialOrder[], startOfToday: Moment) =>
  orders.reduce<{
    past: MaterialOrder[]
    future: MaterialOrder[]
  }>(
    (acc, order) => {
      if (moment.utc(order.scheduledDate).isSameOrAfter(startOfToday)) {
        acc['future'].push(order)
      } else {
        acc['past'].push(order)
      }
      return acc
    },
    {past: [], future: []}
  )

export const getDeliveredIncomingMaterialStatus = (ordered: number, delivered: number) => {
  const DELIVERED_AMOUNT_FRACTION_THRESHOLD = 0.1
  return ordered / (delivered ?? ordered) - 1 < DELIVERED_AMOUNT_FRACTION_THRESHOLD
    ? IncomingMaterialStatus.Delivered
    : IncomingMaterialStatus.Overdue
}

export const mapFutureOrdersToPlannedMaterial = (
  materialOrders: MaterialOrder[]
): PlannedIncomingMaterial[] =>
  materialOrders.map((order) => ({
    status: IncomingMaterialStatus.Planned,
    datetime: moment.utc(order.scheduledDate).startOf('hour'),
    value: order.amount,
    order
  }))

export const mapPastOrdersToDeliveredMaterial = (
  materialOrders: MaterialOrder[]
): DeliveredIncomingMaterial[] =>
  materialOrders.map((order) => {
    const {deliveries, amount, scheduledDate} = order
    const delivered = deliveries.reduce((sum, {amountDelivered}) => sum + amountDelivered, 0)
    const datetime = delivered !== 0 ? deliveries[deliveries.length - 1].deliveredOn : scheduledDate
    return {
      status: getDeliveredIncomingMaterialStatus(amount, delivered),
      datetime: moment.utc(datetime).startOf('hour'),
      value: delivered,
      order
    }
  })

export const mapCanceledOrdersToCancelledMaterial = (
  materialOrders: MaterialOrder[]
): CanceledIncomingMaterial[] =>
  materialOrders.map((order) => ({
    status: IncomingMaterialStatus.Cancelled,
    datetime: moment.utc(order.scheduledDate).startOf('hour'),
    value: 0,
    order
  }))

export const mapUnplannedDeliveriesToUnplannedMaterial = (
  unplannedDeliveries: GroupedUnplannedDelivery[]
): UnplannedIncomingMaterial[] =>
  unplannedDeliveries.map((unplannedDelivery) => ({
    status: IncomingMaterialStatus.Unknown,
    datetime: moment.utc(unplannedDelivery.deliveredOn as string).startOf('hour'),
    value: unplannedDelivery.amountDelivered,
    unplannedDelivery: unplannedDelivery
  }))

export const mapMaterialOrdersToIncomingMaterial = (
  startOfToday: Moment,
  materialOrders: MaterialOrder[],
  unplannedDeliveries: GroupedUnplannedDelivery[]
) => {
  const validMaterialOrders = materialOrders.filter(
    ({status}) => status !== MaterialOrderStatus.Cancelled
  )
  const canceledMaterialOrders = materialOrders.filter(
    ({status}) => status === MaterialOrderStatus.Cancelled
  )

  const {future: futureOrders, past: pastOrders} = splitOrdersToPastAndFuture(
    validMaterialOrders,
    startOfToday
  )

  const plannedIncomingMaterial = mapFutureOrdersToPlannedMaterial(futureOrders)
  const deliveredIncomingMaterial = mapPastOrdersToDeliveredMaterial(pastOrders)
  const canceledIncomingMaterial = mapCanceledOrdersToCancelledMaterial(canceledMaterialOrders)
  const unplannedIncomingMaterial = mapUnplannedDeliveriesToUnplannedMaterial(unplannedDeliveries)

  return {
    planned: plannedIncomingMaterial,
    canceled: canceledIncomingMaterial,
    delivered: deliveredIncomingMaterial,
    unplanned: unplannedIncomingMaterial
  }
}

export const getIncomingMaterialHourly = (
  incomingMaterial: ReturnType<typeof mapMaterialOrdersToIncomingMaterial>
): Record<string, IncomingMaterial[]> => {
  const {planned, canceled, delivered, unplanned} = incomingMaterial

  return [...planned, ...canceled, ...delivered, ...unplanned].reduce<
    Record<string, IncomingMaterial[]>
  >((acc, item) => {
    const key = item.datetime.toISOString()
    acc[key] ? acc[key].push(item as IncomingMaterial) : (acc[key] = [item as IncomingMaterial])
    return acc
  }, {})
}
